import router from "@/router";
import { LocationQueryValue } from "vue-router";

import i18n from "@/plugins/i18n";
import { TErrors } from "@/types/common";
import { useCustomToast } from "@/composables/useCustomToast";
import { getLocaleFromPath } from "@/plugins/localizedRoutes";

const { t } = i18n.global;

export function formatMoneyDecimal(number: any, fix = 0, option = "decimal") {
  let style: string;
  if (["USD", "RUB"]?.includes(option)) {
    style = "currency";
  } else if (["kilogram", "meter", "percent"]?.includes(option)) {
    style = "unit";
  } else {
    style = "";
  }

  const newStyle: string = style;
  const option2 = {
    newStyle, //  unit currency percent decimal
    [newStyle]: option,
    maximumFractionDigits: fix,
    minimumFractionDigits: fix,
  };
  return number ? new Intl.NumberFormat("en-EN", option2).format(number) : "0";
}
export function formatMoneyExact(number, decimals = 4) {
  if (number) {
    // eslint-disable-next-line prefer-const
    let [integerPart, decimalPart] = number.toString().split(".");

    // If there's a decimal part, truncate it to the desired number of decimal places
    if (decimalPart) {
      let truncatedDecimalPart = decimalPart.slice(0, decimals);

      // If truncated decimal part is all zeros, keep checking the rest
      if (Number(`0.${truncatedDecimalPart}`) === 0) {
        const nonZeroIndex = decimalPart.search(/[1-9]/); // Find first non-zero digit
        truncatedDecimalPart = decimalPart.slice(
          0,
          Math.max(nonZeroIndex + 1, decimals),
        );
      }

      decimalPart = truncatedDecimalPart;
    }

    const formatted = decimalPart
      ? `${integerPart}.${decimalPart}`
      : integerPart;

    // Remove trailing zeros and the decimal point if no decimal part remains
    return formatted.replace(/\.?0+$/, "");
  }
  return "0";
}

export function formatNumber(number: number) {
  const strNumber = number.toString();
  const [decimalPart] = strNumber.split(".");

  if (!decimalPart) {
    return strNumber + ".00";
  } else if (/^0+$/.test(decimalPart)) {
    return Number(number).toFixed(2);
  } else {
    const significantDecimal = decimalPart.replace(/0+$/, "");
    if (significantDecimal.length <= 3) {
      return Number(number).toFixed(Math.max(2, significantDecimal.length));
    } else {
      return Number(number).toFixed(5);
    }
  }
}

export function formatPhoneNumber(number: string) {
  const format = number
    ?.replace(/\D/g, "")
    .match(/(\d{0,3})(\d{0,2})(\d{0,3})(\d{0,2})(\d{0,2})/);
  return `+${format && format[1] ? format[1] : ""}
          ${format && format[2] ? format[2] : ""}
          ${format && format[3] ? format[3] : ""}
          ${format && format[4] ? format[4] : ""} ${
            format && format[5] ? format[5] : ""
          }`;
}

export function convertBytes(bytes: number) {
  const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  let unitIndex = 0;
  while (bytes >= 1024 && unitIndex < units.length - 1) {
    bytes /= 1024;
    unitIndex++;
  }

  const unit = units[unitIndex];
  return bytes?.toFixed(2) + " " + unit;
}

export function formatTime(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  // Pad single-digit hours, minutes, and seconds with leading zeros if needed
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

  return `${formattedHours} часа, ${formattedMinutes} мин, ${formattedSeconds} сек`;
}

export function secondsToTime(seconds: number, isHours?: boolean) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const hoursStr = String(hours).padStart(2, "0");
  const minutesStr = String(minutes).padStart(2, "0");
  const secondsStr = String(remainingSeconds).padStart(2, "0");

  return isHours
    ? `${hoursStr}:${minutesStr}:${secondsStr}`
    : `${minutesStr}:${secondsStr}`;
}

const timeouts: { [key: string]: any } = {};

const cTimeout = (key = "key") => {
  if (timeouts[key]) {
    clearTimeout(timeouts[key]);
    timeouts[key] = undefined;
  }
};
// eslint-disable-next-line
export const debounce = (key = "key", fn = () => {}, timeout = 500) => {
  const sTimeout = (key: string, fn: any, timeout: number) => {
    cTimeout(key);

    timeouts[key] = setTimeout(() => {
      try {
        fn();
      } catch (e) {
        console.log(e);
      }

      timeouts[key] = undefined;
    }, timeout);
  };

  return sTimeout(key, fn, timeout);
};

export async function updateQueryParams(
  key: string,
  value: LocationQueryValue | LocationQueryValue[],
) {
  const route = router.currentRoute.value;
  const queryParams = { ...route.query };
  if (queryParams[key]) {
    queryParams[key] = value;
  } else {
    queryParams[key] = value;
  }

  // Get current locale from path
  const currentLocale = getLocaleFromPath(route.path);

  // If the route has a name, prepend the locale to it (if not already prefixed)
  let routeName = route.name?.toString() || "Dashboard";
  if (!routeName.startsWith(`${currentLocale}-`)) {
    routeName = `${currentLocale}-${routeName}`;
  }

  await router.replace({ name: routeName, query: queryParams });
}

export function handleError(errors: TErrors) {
  const { showToast } = useCustomToast();
  try {
    const errorMessage = t(errors?.errors[0]?.error || t("error"));
    showToast(errorMessage, "error");
  } catch {
    showToast(t("error"), "error");
  }
}

export function formatDateRightOrder(date: string) {
  const dateObj = date.split(".");
  return `${dateObj[1]}.${dateObj[0]}.${dateObj[2]}`;
}

export function formatDuration(seconds: number) {
  const days = Math.floor(seconds / (3600 * 24));
  seconds %= 3600 * 24;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];

  if (days > 0) {
    parts.push(`${days} ${t("days")}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${t("hours")}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${t("minutes")}`);
  }
  if (remainingSeconds > 0) {
    parts.push(`${remainingSeconds.toFixed(1)} ${t("seconds")}`);
  }

  return parts.join(", ");
}

export function convertToStars(text: string) {
  return text?.replace(/[^*]/g, "*");
}

export const moneyMask = {
  mask: [
    "D",
    "D#",
    "D##",
    "D ###",
    "D# ###",
    "D## ###",
    "D ### ###",
    "D# ### ###",
    "D## ### ###",
    "D ### ### ###",
  ],
  tokens: {
    D: {
      pattern: /[1-9]/,
    },
  },
};
export const getObjectWithHighestTotalOrders = (array) => {
  if (array.length === 0) return null;
  return array.reduce((max, obj) =>
    obj.total_orders > max.total_orders ? obj : max,
  );
};
export const checkProfit = (profit: number) => {
  if (profit < 0) {
    return {
      color: "bg-red/20",
      textColor: "stroke-red !text-red",
      position: "rotate-180",
      text: "losses",
      bgColor: "bg-red/10 px-1 py-0.5 rounded-md",
    };
  } else {
    return {
      color: "bg-green/20",
      textColor: "stroke-green !text-green",
      position: "",
      text: "income",
      bgColor: "bg-green/10 px-1 py-0.5 rounded-md",
    };
  }
};

export function getMobileOperatingSystem() {
  if (typeof window !== "undefined") {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/windows phone/i.test(userAgent)) {
      return "Windows Phone";
    }

    if (/android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }
  }

  return "unknown";
}

export function formatSliceNumber(number: number) {
  if (number >= 1000) {
    const suffixes = ["", "K", "M", "B", "T"];
    const magnitude = Math.floor(Math.log10(number) / 3);
    const scaledNumber = number / Math.pow(1000, magnitude);
    const fixedDigits = scaledNumber < 10 ? 1 : 0;
    return scaledNumber.toFixed(fixedDigits) + suffixes[magnitude];
  } else {
    return String(number);
  }
}
